import {
  generateHouseHoldNumberCascader,
  Option,
} from '@/utils/generateHouseHoldNumberCascader';
import { Table, Cascader, TableProps, Button } from 'antd';
import moment from 'moment';
import React, { useState, useEffect, useMemo, useContext } from 'react';

import { Tab } from '@alifd/next';
import { Box } from '@mui/material';
import { PostalRegistration } from '@/types/database/postalRegistration';
import { DepositRegistration } from '@/types/database/depositRegistration';
import { BackStageContext } from '@/contexts/BackStageContext';
import PageHeader from '@/components/PageHeader';
import useService from '@/lib/hooks/useService';

import { TableContainer } from './styles';
import { getSorter } from './utils';

const DeliveryList: React.FC = () => {
  const [deposit, updateDeposit] = useService<DepositRegistration>({
    collectionName: 'deposit',
  });
  const [postal, updatePostal] = useService<PostalRegistration>({
    collectionName: 'postalRegistration',
  });

  const { backStageUpdateTrigger } = useContext(BackStageContext);
  const [filterHouseOptions, setFilterHouseOptions] = useState<Option[]>([]);
  const [filter, setFilter] = useState<{
    house: string[];
  }>({
    house: [],
  });
  const [filteredPostalData, setFilteredPostalData] = useState<
    PostalRegistration[]
  >([]);
  const [filteredDepositData, setFilteredDepositData] = useState<
    DepositRegistration[]
  >([]);
  const [tabStatus, setTabStatus] = useState<'郵件包裹' | '寄物'>('郵件包裹');
  useEffect(() => {
    updateDeposit();
    updatePostal();
  }, [backStageUpdateTrigger, updateDeposit, updatePostal]);

  useEffect(() => {
    const newHouseNumbers: string[] = [];
    postal.forEach((pile) => {
      if (typeof pile.household === 'string') {
        newHouseNumbers.push(pile.household);
      }
    });
    setFilterHouseOptions(
      generateHouseHoldNumberCascader(
        newHouseNumbers.sort((a, b) => a.localeCompare(b))
      )
    );
  }, [postal]);

  useEffect(() => {
    const newPostalData = postal
      .filter((pile) => pile.robotDelivery === true)
      .filter((pile) => {
        if (
          filter.house &&
          filter.house.length > 0 &&
          pile.household !== filter.house.join('-')
        ) {
          return false;
        }

        return true;
      });
    const newDepositData = deposit
      .filter((pile) => pile.robotDelivery === true)
      .filter((pile) => {
        if (
          filter.house &&
          filter.house.length > 0 &&
          pile.household !== filter.house.join('-')
        ) {
          return false;
        }

        return true;
      });
    setFilteredPostalData(newPostalData);
    setFilteredDepositData(newDepositData);
  }, [filter, postal, deposit]);

  const columns: TableProps<PostalRegistration>['columns'] = useMemo(
    () => [
      {
        title: '類型',
        dataIndex: 'packageType',
        sorter: getSorter('packageType'),
        render: (value) => (value ? '郵件包裹' : '寄物'),
      },
      {
        title: '包裹編號',
        dataIndex: 'order',
        sorter: getSorter('order'),
      },
      {
        title: '戶號',
        dataIndex: 'household',
        sorter: getSorter('household'),
      },

      {
        title: '收件人',
        dataIndex: 'recipient',
        sorter: getSorter('recipient'),
      },
      {
        title: '狀態',
        dataIndex: 'status',
        sorter: getSorter('status'),
      },
      {
        title: '更新日期',
        dataIndex: 'updatedTime',
        render: (value) => moment(value).format('YYYY-MM-DD HH:mm:ss'),
        sorter: getSorter('updatedTime'),
      },
    ],
    []
  );

  const checkDataCategory = (
    item: PostalRegistration | DepositRegistration
  ) => {
    if (tabStatus === '郵件包裹') {
      const typedItem = item as PostalRegistration;
      return (
        typedItem?.packageType &&
        typedItem.status === '已領取' &&
        typedItem.robotDelivery
      );
    }
    return item.status === '已領取' && item.robotDelivery;
  };

  return (
    <Box>
      {/* 標題 */}
      <PageHeader
        title="送件歷史記錄"
        breadcrumbs={[{ name: '送件服務' }, { name: '送件歷史記錄' }]}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          marginBottom: '10px',
        }}
      >
        <Cascader
          value={filter.house}
          onChange={(value) =>
            setFilter({
              ...filter,
              house: value ? value.map((v) => v.toString()) : [],
            })
          }
          options={filterHouseOptions}
          placeholder="請選擇戶號"
          allowClear
          style={{ width: 200 }}
        />
      </Box>
      <Tab
        onChange={(key) =>
          setTabStatus(key === '郵件包裹' ? '郵件包裹' : '寄物')
        }
        style={{ marginBottom: 10 }}
      >
        <Tab.Item title="郵件包裹" key="郵件包裹" />
        <Tab.Item title="寄物" key="寄物" />
      </Tab>
      <TableContainer>
        <Table
          bordered
          dataSource={[...filteredPostalData, ...filteredDepositData]
            .sort((a, b) => {
              return b.updatedTime - a.updatedTime;
            })
            .filter((item) => checkDataCategory(item))}
          columns={columns}
          showSorterTooltip={false}
          pagination={{
            pageSize: 10,
          }}
          sortDirections={['descend', 'ascend', 'descend']}
        />
      </TableContainer>
    </Box>
  );
};

export default DeliveryList;
